// FlixPlayerDisturber.js
// Leaker detection component

import { throttle } from 'lodash';

const disturbInterval = 60 * 1000;
const disturbPlaybackRates = [0.5, 1.5, 2];

/** @typedef {import('video.js').VideoJsPlayer} VideoJsPlayer */
/** @typedef {'shuffleTime'|'shufflePlaybackRate'} DisturbAction */

/**
 * @typedef OnDisturbedArgs
 * @property {DisturbAction} action
 * @property {number} currentTime
 * @property {number?} shuffledTime
 * @property {number?} shuffledPlaybackRate
 */

/** @typedef {(args: OnDisturbedArgs)=>void} OnDisturbedCallback */

export default class FlixPlayerDisturber {
  /** @type {VideoJsPlayer} */
  player = null;
  /** @type {OnDisturbedCallback} */
  onDisturbed = null;

  lastDisturbedTime = 0;

  /**
   * @param {VideoJsPlayer} player
   * @param {OnDisturbedCallback} onDisturbed
   */
  static mount(player, onDisturbed) {
    const disturber = new FlixPlayerDisturber(player, onDisturbed);
    disturber.activate();
    return disturber;
  }

  /**
   * @param {VideoJsPlayer} player
   * @param {OnDisturbedCallback} onDisturbed
   */
  constructor(player, onDisturbed) {
    this.player = player;
    this.onDisturbed = onDisturbed;
  }

  activate() {
    this.lastDisturbedTime = Date.now();
    this.player.on('timeupdate', this.handleTimeUpdate);
  }

  deactivate() {
    this.player.off('timeupdate', this.handleTimeUpdate);
  }

  handleTimeUpdate = throttle(() => {
    const now = Date.now();
    if (now - this.lastDisturbedTime >= disturbInterval) {
      this.disturb();
      this.lastDisturbedTime = now;
    }
  }, 1000); // TODO: remote config

  disturb = () => {
    const actionIndex = Math.floor(Math.random() * 2);
    const currentTime = this.player.currentTime();
    if (actionIndex === 0) {
      // shuffle time
      const shuffledTime = Math.random() * this.player.duration();
      this.player.currentTime(shuffledTime);

      this.onDisturbed?.({
        action: 'shuffleTime',
        currentTime: currentTime,
        shuffledTime,
      });
    } else if (actionIndex === 1) {
      // shuffle playback-rate
      const shuffledPlaybackRate =
        disturbPlaybackRates[
          Math.floor(Math.random() * disturbPlaybackRates.length)
        ];
      this.player.playbackRate(shuffledPlaybackRate);

      this.onDisturbed?.({
        action: 'shufflePlaybackRate',
        currentTime: currentTime,
        shuffledPlaybackRate,
      });
    }
  };
}
